import React, { useState, useEffect, useCallback } from 'react';
import { teamAPI } from './services/api';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';

export default function App() {
  // Extract token from URL path (after /rdv/) or query param
  const getTokenFromPath = () => {
    const path = window.location.pathname;
    const segments = path.split('/').filter(Boolean);
    if (segments.length >= 1 && segments[0] !== 'rdv') return segments[0];
    if (segments.length >= 2 && segments[0] === 'rdv') return segments[1];
    return null;
  };

  const [advisorToken, setAdvisorToken] = useState(() => {
    const pathToken = getTokenFromPath();
    if (pathToken) return pathToken;
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  });

  const [view, setView] = useState('login'); // 'login' | 'dashboard' | 'settings'
  const [user, setUser] = useState(null);
  const [advisorPassword, setAdvisorPassword] = useState('');
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Extract token from URL on load
  useEffect(() => {
    const pathToken = getTokenFromPath();
    if (pathToken) { setAdvisorToken(pathToken); return; }
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (token) setAdvisorToken(token);
  }, []);

  const handleLogin = useCallback(async (password) => {
    setLoading(true); setError('');
    try {
      const res = await teamAPI.login(advisorToken, password);
      if (res.token && res.member) {
        localStorage.setItem('team_token', res.token);
        localStorage.setItem('team_info', JSON.stringify(res.member));
        setUser(res.member);

        // Check if advisor (has isAdvisor flag or role === 'advisor')
        if (res.member.isAdvisor || res.member.role === 'advisor') {
          // Advisor login - fetch team data
          const teamRes = await teamAPI.getTeam(advisorToken, password);
          setAdvisorPassword(password);
          localStorage.setItem('advisor_password', password);
          setTeamData(teamRes);
        }
        setView('dashboard');
      } else {
        setError('Connexion échouée');
      }
    } catch (e) {
      setError(e?.error || 'Mot de passe incorrect');
    } finally { setLoading(false); }
  }, [advisorToken]);

  const handleLogout = () => {
    localStorage.removeItem('team_token');
    localStorage.removeItem('team_info');
    localStorage.removeItem('advisor_password');
    setUser(null);
    setView('login');
  };

  const handleBackToDashboard = () => {
    setView('dashboard');
  };

  // Restore session on load
  useEffect(() => {
    const token = localStorage.getItem('team_token');
    const info = localStorage.getItem('team_info');
    const advisorPw = localStorage.getItem('advisor_password');
    if (token && info) {
      try {
        const member = JSON.parse(info);
        setUser(member);
        if (member.isAdvisor || member.role === 'advisor') {
          const advisorPw = localStorage.getItem('advisor_password');
          if (advisorPw) setAdvisorPassword(advisorPw);
        }
        setView('dashboard');
      } catch { handleLogout(); }
    }
  }, [handleLogout]);

  if (!advisorToken) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#f8f7fc' }}>
        <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(124,58,237,0.1)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔗</div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#7c3aed', marginBottom: 8 }}>Lien invalide</h1>
          <p style={{ color: '#64748b' }}>Ce lien d'équipe est invalide ou expiré. Contactez votre conseiller pour obtenir le bon lien.</p>
        </div>
      </div>
    );
  }

  // Show Settings button only if team has members (team enabled)
  const showSettings = user && (user.isAdvisor || user.role === 'advisor') && teamData?.members?.length > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f7fc' }}>
      {view === 'login' && (
        <LoginScreen
          onLogin={handleLogin}
          loading={loading}
          error={error}
        />
      )}
      {view === 'dashboard' && user && (
        <Dashboard
          user={user}
          teamData={teamData}
          advisorToken={advisorToken}
          advisorPassword={advisorPassword}
          showSettings={showSettings}
          onSettings={() => setView('settings')}
          onLogout={handleLogout}
        />
      )}
      {view === 'settings' && user && teamData && (
        <Settings
          teamData={teamData}
          advisorToken={advisorToken}
          advisorPassword={advisorPassword}
          onBack={handleBackToDashboard}
        />
      )}
    </div>
  );
}